import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import {
  IMAGES,
  STATS,
  COMMITMENT_VALUES,
} from "@/lib/data";
import CtaBand from "@/components/CtaBand";
import TimelineCarousel from "@/components/TimelineCarousel";

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section data-testid="hero-section" className="relative overflow-hidden" style={{ background: "var(--zt-bg-alt)" }}>
        {/* Decorative grid */}
        <div
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(to right, #24356E 1px, transparent 1px), linear-gradient(to bottom, #24356E 1px, transparent 1px)",
            backgroundSize: "56px 56px",
          }}
        />
        {/* Diagonal teal accent (constrained inside hero) */}
        <div
          className="absolute -top-10 -right-10 w-40 md:w-96 h-40 md:h-96 opacity-10 pointer-events-none rotate-45 max-w-[35vw]"
          style={{ background: "linear-gradient(135deg, #3397AC, transparent)" }}
        />

        <div className="zt-container pt-8 md:pt-20 pb-10 md:pb-20 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center relative">
          <div className="lg:col-span-7 order-2 lg:order-1">
            <div className="flex items-start gap-2 mb-4 md:mb-6 zt-fade-up">
              <span className="inline-block w-6 md:w-10 h-px flex-shrink-0 mt-2 md:mt-2.5" style={{ background: "var(--zt-teal)" }} />
              <p className="zt-tagline zt-overline">Better Health. Stronger Communities.</p>
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight leading-[1.05] zt-fade-up">
              Building The Next Generation of{" "}
              <span className="relative inline-block">
                <span className="relative z-10" style={{ color: "var(--zt-navy)" }}>Healthcare</span>
                <span className="absolute bottom-1 left-0 right-0 h-3" style={{ background: "rgba(51, 151, 172, 0.28)", zIndex: 0 }} />
              </span>
            </h1>
            <p className="text-base md:text-lg text-slate-600 leading-relaxed mt-5 md:mt-6 max-w-2xl zt-fade-up">
              ZT Health is a healthcare services platform within&nbsp;
              <a
                href="https://ztcorporate.com/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-ztcorp-link"
                className="font-semibold border-b border-slate-300 hover:border-slate-900 pb-0.5 transition-colors"
                style={{ color: "var(--zt-navy)" }}
              >
                ZT Corporate
              </a>
              , focused on operating and growing businesses that expand access to care. Our portfolio spans micro-hospitals, freestanding emergency centers, and physician staffing.
            </p>

            <div className="mt-6 md:mt-8 flex flex-wrap gap-3 md:gap-4 zt-fade-up">
              <Link
                to="/services"
                data-testid="hero-cta-services"
                className="zt-btn-primary inline-flex items-center gap-2"
              >
                Explore Services <ArrowRight size={16} />
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5 zt-fade-up order-1 lg:order-2">
            <div className="relative">
              <img
                src={IMAGES.heroBuilding}
                alt="ZT Health physician with patient"
                className="w-full h-[260px] sm:h-[340px] lg:h-[560px] object-cover"
                style={{ objectPosition: "center top" }}
              />
              {/* Geometric corner accent */}
              <div className="absolute top-0 right-0 w-12 md:w-16 h-12 md:h-16" style={{ background: "var(--zt-teal)" }} />
              <div className="absolute bottom-0 left-0 w-20 md:w-24 h-2" style={{ background: "var(--zt-navy)" }} />
            </div>
          </div>
        </div>
      </section>

      {/* OUR PORTFOLIO — angled 3-panel showcase (subtle teal tint) */}
      <section data-testid="portfolio-showcase" className="zt-section" style={{ background: "rgba(51, 151, 172, 0.06)" }}>
        <div className="zt-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12 items-end">
            <div className="lg:col-span-7">
              <p className="zt-overline mb-5">Our Portfolio</p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-tight">
                Three platforms. One unified healthcare strategy
              </h2>
            </div>
            <div className="lg:col-span-5">
              <p className="text-base md:text-lg text-slate-600 leading-relaxed">
                ZT Health invests in resilient healthcare verticals with long-term growth potential, building durable platforms that expand access, strengthen clinical delivery, and serve communities across Texas and beyond.
              </p>
            </div>
          </div>

          {/* 3 angled panels — stacked on mobile, angled 3-up on desktop */}
          <div className="relative w-full overflow-hidden">
            <div
              className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-5 lg:[grid-auto-rows:460px] auto-rows-[240px] sm:auto-rows-[280px] md:auto-rows-[320px]"
              data-testid="portfolio-panels"
            >
              {[
                {
                  label: "Freestanding Emergency Rooms",
                  href: "/services#freestanding-er",
                  image:
                    "https://customer-assets.emergentagent.com/job_corp-web-launch/artifacts/or75cnti_Clinical%204.jpg",
                  clipDesktop: "polygon(0 0, 100% 0, 88% 100%, 0 100%)",
                  labelCenter: "44%",
                  objectFit: "contain",
                  blurFill: true,
                },
                {
                  label: "Micro-Hospitals",
                  href: "/services#micro-hospitals",
                  image:
                    "https://customer-assets-v7afamib.emergentagent.net/job_corp-web-launch/artifacts/lbgf3n29_Lumberton-main-image.webp",
                  clipDesktop: "polygon(12% 0, 100% 0, 88% 100%, 0 100%)",
                  labelCenter: "50%",
                  objectFit: "cover",
                  blurFill: false,
                },
                {
                  label: "Physician Staffing",
                  href: "/services#physician-staffing",
                  image:
                    "https://customer-assets.emergentagent.com/job_corp-web-launch/artifacts/mhh9svxz_Clinical%201.jpg",
                  clipDesktop: "polygon(12% 0, 100% 0, 100% 100%, 0 100%)",
                  labelCenter: "56%",
                  objectFit: "contain",
                  blurFill: true,
                },
              ].map((panel, i) => (
                <Link
                  key={panel.label}
                  to={panel.href}
                  data-testid={`portfolio-panel-${i}`}
                  className="portfolio-panel relative group block h-full overflow-hidden"
                  style={{ "--clip-desktop": panel.clipDesktop }}
                >
                  {panel.blurFill && (
                    <img
                      src={panel.image}
                      alt=""
                      aria-hidden="true"
                      className="absolute inset-0 w-full h-full"
                      style={{
                        objectFit: "cover",
                        objectPosition: "center",
                        filter: "blur(40px) saturate(1.1)",
                        transform: "scale(1.35)",
                      }}
                      loading="lazy"
                    />
                  )}
                  <img
                    src={panel.image}
                    alt={panel.label}
                    className="absolute inset-0 w-full h-full transition-transform duration-700 group-hover:scale-105"
                    style={{
                      objectFit: panel.objectFit || "cover",
                      objectPosition: "center",
                      ...(panel.blurFill
                        ? {
                            WebkitMaskImage:
                              "radial-gradient(ellipse 95% 105% at 50% 50%, rgba(0,0,0,1) 70%, rgba(0,0,0,0.85) 85%, rgba(0,0,0,0) 100%)",
                            maskImage:
                              "radial-gradient(ellipse 95% 105% at 50% 50%, rgba(0,0,0,1) 70%, rgba(0,0,0,0.85) 85%, rgba(0,0,0,0) 100%)",
                          }
                        : {}),
                    }}
                    loading="lazy"
                  />
                  <div
                    className="absolute inset-0 transition-opacity duration-500"
                    style={{
                      background:
                        "linear-gradient(180deg, rgba(255,255,255,0) 60%, rgba(255,255,255,0.35) 100%)",
                    }}
                  />
                  {/* Label — full-width on mobile bottom, floating on desktop */}
                  <div
                    className="panel-label absolute bottom-4 md:bottom-6 left-4 right-4"
                    style={{ "--desktop-left": panel.labelCenter }}
                  >
                    <div className="bg-white px-4 py-3 md:px-6 md:py-4 transition-all duration-300 group-hover:-translate-y-1 group-hover:shadow-[0_12px_40px_rgba(15,23,42,0.18)] text-center flex items-center justify-center panel-label-box">
                      <p
                        className="text-[11px] sm:text-xs md:text-sm font-bold uppercase tracking-[0.14em] leading-tight"
                        style={{ color: "var(--zt-navy)", fontFamily: "Outfit, sans-serif" }}
                      >
                        {panel.label}
                      </p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* STATS BAND */}
      <section data-testid="stats-band" className="border-t border-b border-slate-200" style={{ background: "var(--zt-navy)" }}>
        <div className="zt-container py-10 md:py-12 grid grid-cols-2 md:grid-cols-4 gap-y-8 divide-x divide-white/10">
          {STATS.map((s) => (
            <div key={s.label} data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`} className="px-4 md:px-8 text-center md:text-left">
              <p className="text-3xl md:text-5xl font-bold tracking-tight text-white" style={{ fontFamily: "Outfit, sans-serif" }}>
                {s.value}
              </p>
              <p className="text-xs md:text-sm mt-2 uppercase tracking-wider" style={{ color: "#3397AC" }}>{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* WHY HEALTHCARE — concise investment thesis */}
      <section data-testid="why-healthcare" className="zt-section border-b border-slate-200">
        <div className="zt-container grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-center">
          <div className="lg:col-span-7 order-2 lg:order-1">
            <p className="zt-overline mb-4">Why Healthcare</p>
            <h2 className="text-2xl md:text-4xl font-bold tracking-tight leading-tight mb-5">
              A resilient sector built for long-term value
            </h2>
            <p className="text-base md:text-lg text-slate-600 leading-relaxed">
              Healthcare remains one of the most durable sectors of the economy, supported by consistent demand and the essential nature of the services delivered. ZT Health invests in this resilience, building operator-led platforms that expand access, empower physicians, and generate long-term value for the communities we serve.
            </p>
            <Link
              to="/about"
              data-testid="why-healthcare-learn-more"
              className="inline-flex items-center gap-2 mt-5 text-sm font-bold uppercase tracking-wider border-b pb-0.5 transition-colors hover:opacity-80"
              style={{ color: "var(--zt-teal)", borderColor: "var(--zt-teal)" }}
            >
              Learn About Our Approach <ArrowRight size={14} />
            </Link>
          </div>
          <div className="lg:col-span-5 order-1 lg:order-2">
            <img
              src="https://customer-assets-v7afamib.emergentagent.net/job_corp-web-launch/artifacts/d6c5i27y_10068%20-%20ZTW%20Altus%20Stock%20Session%202026-HDR.webp"
              alt="Altus Emergency Center exterior"
              className="w-full h-[220px] sm:h-[260px] lg:h-[360px] object-cover"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* OUR ORIGINS */}
      <section data-testid="our-origins" className="zt-section">
        <div className="zt-container grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-7">
            <p className="zt-overline mb-4">Our Origins</p>
            <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-tight mb-5">
              A Diversified Healthcare Portfolio Built for Sustainable Growth
            </h2>
            <p className="text-base md:text-lg text-slate-600 leading-relaxed">
              What began as a retail brokerage firm serving physicians has evolved over more than two decades into a diversified healthcare platform focused on community-based care through micro-hospitals, freestanding emergency rooms, and physician staffing.
            </p>

            <div className="grid grid-cols-2 gap-6 mt-8 max-w-md">
              <div className="border-l-4 pl-4" style={{ borderColor: "var(--zt-teal)" }}>
                <p className="text-3xl md:text-4xl font-bold" style={{ color: "var(--zt-navy)" }}>2MM+</p>
                <p className="text-sm text-slate-600 mt-1">Patients Served</p>
              </div>
              <div className="border-l-4 pl-4" style={{ borderColor: "var(--zt-teal)" }}>
                <p className="text-3xl md:text-4xl font-bold" style={{ color: "var(--zt-navy)" }}>23</p>
                <p className="text-sm text-slate-600 mt-1">Years in Service</p>
              </div>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative">
              <img
                src={IMAGES.facilityInterior}
                alt="Modern medical facility"
                className="w-full h-[220px] sm:h-[260px] lg:h-[480px] object-cover"
              />
              <div className="absolute -bottom-4 lg:-bottom-6 -right-4 lg:-right-6 w-20 lg:w-32 h-20 lg:h-32 -z-10" style={{ background: "var(--zt-teal)" }} />
              <div className="absolute -top-4 lg:-top-6 -left-4 lg:-left-6 w-14 lg:w-20 h-14 lg:h-20 -z-10" style={{ background: "var(--zt-navy)" }} />
            </div>
          </div>
        </div>
      </section>

      {/* OUR COMMITMENT */}
      <section data-testid="our-commitment" className="zt-section">
        <div className="zt-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end mb-16">
            <div className="lg:col-span-7">
              <p className="zt-overline mb-5">Our Commitment</p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-tight">
                Five principles that shape every decision we make
              </h2>
            </div>
          </div>
          <div className="flex flex-wrap justify-center gap-6">
            {COMMITMENT_VALUES.map((v, i) => {
              const accents = ["#24356E", "#3397AC", "#24356E", "#3397AC", "#24356E"];
              return (
                <div
                  key={v.title}
                  data-testid={`commitment-${i}`}
                  className="bg-white border border-slate-200 p-8 group hover:shadow-[0_12px_40px_rgba(15,23,42,0.08)] hover:-translate-y-1 transition-all duration-300 relative w-full sm:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] min-w-[240px]"
                >
                  <div
                    className="absolute top-0 left-0 h-1 w-12 transition-all duration-300 group-hover:w-full"
                    style={{ background: accents[i] }}
                  />
                  <h3 className="text-lg font-semibold mb-3 leading-tight">{v.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{v.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* HISTORY TIMELINE — HORIZONTAL CAROUSEL */}
      <section data-testid="history-timeline" className="zt-section border-t border-slate-200" style={{ background: "var(--zt-bg-alt)" }}>
        <div className="zt-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end mb-12">
            <div className="lg:col-span-7">
              <p className="zt-overline mb-5">ZT Health History</p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-tight">
                Two decades of disciplined growth
              </h2>
            </div>
            <div className="lg:col-span-5">
              <p className="text-slate-600 leading-relaxed">
                From a single imaging center in Houston to a multi-vertical healthcare platform. Swipe through every milestone in our journey.
              </p>
            </div>
          </div>

          <TimelineCarousel />
        </div>
      </section>

      <CtaBand />
    </div>
  );
}
